/**
 * Consent Banner - Lightweight GDPR cookie consent with Google Consent Mode v2
 * @author Pavel Szabo
 * @license MIT
 */

import { ConsentManager } from './ConsentManager.js';
import { UI } from './UI.js';
import { translations } from './i18n/index.js';

// Auto-initialize when DOM is ready
function init() {
  // Find script tag with config
  const script = document.querySelector('script[data-consent-banner]');

  if (!script) {
    console.warn('ConsentBanner: No script tag with data-consent-banner found');
    return;
  }

  // Společná konfigurace z inline snippetu (window.psConsent.config): jeden zdroj názvu a platnosti
  // souhlasové cookie. Atribut na liště má přednost (staré weby beze snippetu beze změny).
  const shared = (window.psConsent && window.psConsent.config) || {};
  const days = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  // Parse config from data attributes
  const config = {
    lang: script.dataset.lang || document.documentElement.lang || 'cs',
    primaryColor: script.dataset.primaryColor || '#2563eb',
    primaryColorHover: script.dataset.primaryColorHover || null, // Auto-generated if not set
    policyUrl: script.dataset.policyUrl || '/cookies/',
    position: script.dataset.position || 'bottom', // bottom, top, center
    floatingPosition: script.dataset.fabPosition || 'left', // left, right
    fabBottom: script.dataset.fabBottom || null, // Custom bottom position (e.g., '80px')
    fabBottomMobile: script.dataset.fabBottomMobile || null, // Custom bottom position on mobile (e.g., '80px')
    categories: JSON.parse(script.dataset.categories || '["necessary","analytics","marketing"]'),
    cookieName: script.dataset.cookieName || shared.cookieName || 'consent_preferences',
    cookieExpiry: days(script.dataset.cookieExpiry, days(shared.cookieExpiry, 365)),
    debug: script.dataset.debug === 'true',
    // Google Consent Mode: data-gcm-defaults="off" = the page sends consent default (and the set
    // commands) itself in an inline snippet before gtag.js (src/consent-default.inline.js), the banner
    // then only sends consent update. Default "on" keeps the v1.0 behaviour.
    gcmDefaults: script.dataset.gcmDefaults !== 'off',
    // data-url-passthrough="false" = the banner does not send gtag('set', 'url_passthrough', true).
    urlPassthrough: script.dataset.urlPassthrough !== 'false',
    // Optional branding
    logo: script.dataset.logo || null, // URL to logo image
    companyName: script.dataset.companyName || null, // Company name for title
  };

  // Initialize
  const t = translations[config.lang] || translations.cs;
  const manager = new ConsentManager(config, t);
  const ui = new UI(manager, config, t);

  // Expose to global scope for manual control
  window.ConsentBanner = {
    manager,
    ui,
    show: () => ui.showBanner(),
    showSettings: () => ui.showModal(),
    getConsent: () => manager.getConsent(),
    hasConsent: (category) => manager.hasConsent(category),
    reset: () => {
      manager.reset();
      ui.removeFloatingButton();
      ui.showBanner();
    },
  };

  // Check if consent already given
  if (!manager.hasStoredConsent()) {
    ui.showBanner();
  } else {
    // Apply stored consent
    manager.applyStoredConsent();
    ui.createFloatingButton();
  }
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Note: window.ConsentBanner is set in init() with public API
// No exports needed - this is a standalone script
